import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { ArrowDownTrayIcon, ArrowUpTrayIcon, BuildingLibraryIcon, ExclamationCircleIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, Card, Container, FileUpload, MetricCard, PageHeader, Select, Table } from "../../ui";
import { errorMessageOf, problemOf } from "../../../api/scholarships";
import { bursariesApi } from "../../../api/hliLists";
import { parseExcelFile } from "../../SpreadsheetUpload";
import { formatDateTime } from "../review/model";
import { PUBLIC_LOANS_KEY, SHEET_COLUMNS, rowProblemsOf, useUploads } from "./model";
import { useAcademicYears, useCurrentAcademicYear } from "../../../hooks/useAcademicYears";

// An empty sheet with the upload's columns
const downloadTemplate = () => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([SHEET_COLUMNS]), "Students");
  XLSX.writeFile(workbook, "students-upload-template.xlsx");
};

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const uploadColumns = (withInstitution) => [
  ...(withInstitution
    ? [{ key: "institution", label: "Institution", accessor: (u) => u.institution.name, filter: "Any institution", render: (u) => <span className="font-medium text-slate-800">{u.institution.name}</span> }]
    : []),
  { key: "year", label: "Academic year", accessor: (u) => u.academicYear?.name, filter: "Any year" },
  { key: "file", label: "File", accessor: (u) => u.fileName },
  { key: "added", label: "New students", accessor: (u) => u.studentsAdded, sortValue: (u) => u.studentsAdded },
  { key: "updated", label: "Updated", accessor: (u) => u.studentsUpdated, sortValue: (u) => u.studentsUpdated },
  { key: "uploaded", label: "Uploaded", accessor: (u) => `${formatDateTime(u.uploadedAt)} · ${u.uploadedBy}`, sortValue: (u) => u.uploadedAt },
];

// The HLI admin uploads their institution's admitted students for a year in one sheet; students then apply for loans
export function HliPublicLoansPage() {
  const queryClient = useQueryClient();
  const { data = [], isPending, isError } = useUploads("hli");
  // The years in HEC's list, the ones ahead too (admissions come before the year starts); the upload names its year as it is named there
  const { data: academicYears = [] } = useAcademicYears({ includeFuture: true });
  const { data: current } = useCurrentAcademicYear();
  const yearOptions = academicYears.map((y) => ({ value: y.academicYearName, label: y.academicYearName }));
  const [chosenYear, setYear] = useState("");
  const year = chosenYear || current?.academicYearName || yearOptions[0]?.value || "";
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [rowProblems, setRowProblems] = useState([]);
  const [busy, setBusy] = useState(false);

  const choose = (selected) => {
    setRowProblems([]);
    setError("");
    if (selected && !["xlsx", "xls"].includes(selected.name.split(".").pop().toLowerCase())) {
      setFile(null);
      setError("Choose an Excel file (.xlsx or .xls).");
      return;
    }
    setFile(selected);
  };

  const upload = async () => {
    setBusy(true);
    setError("");
    setRowProblems([]);
    try {
      const rows = await parseExcelFile(file);
      if (!rows.length) throw new Error("The sheet has no rows under its header.");
      const { data: saved } = await bursariesApi.upload({ academicYear: year, fileName: file.name, rows });
      toast.success(`${year}: ${plural(saved.studentsAdded, "new student")}, ${saved.studentsUpdated} updated`);
      queryClient.invalidateQueries({ queryKey: PUBLIC_LOANS_KEY });
      setFile(null);
    } catch (err) {
      console.error(`Failed to upload the students sheet for ${year}:`, err);
      const problems = rowProblemsOf(problemOf(err));
      setRowProblems(problems);
      setError(problems.length ? `${problems.length} problem${problems.length === 1 ? "" : "s"} in the sheet. Nothing was saved.` : err.response ? errorMessageOf(err, "Failed to upload the sheet.") : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Upload Students"
        subtitle="Your institution's admitted students for the year, in one sheet. Students then apply for a loan, which you grant or reject in Loan Approval."
        actions={
          <Button variant="outline" onClick={downloadTemplate} icon={<ArrowDownTrayIcon className="h-4 w-4" />}>
            Download template
          </Button>
        }
      />

      <Card className="max-w-3xl" bodyClassName="flex flex-col gap-5">
        <Select label="Academic year" required options={yearOptions} value={year} onChange={(e) => setYear(e.target.value)} />
        <FileUpload label="Excel file" required accept=".xlsx,.xls" hint="XLSX or XLS" file={file} onChange={busy ? () => {} : choose} error={error} />
        <Button fullWidth onClick={upload} disabled={!file || !year} loading={busy} icon={<ArrowUpTrayIcon className="h-4 w-4" />}>
          Upload sheet
        </Button>
        {rowProblems.length > 0 && (
          <ul className="thin-scrollbar flex max-h-60 flex-col gap-2 overflow-y-auto rounded-lg bg-red-50 p-3">
            {rowProblems.map((p) => (
              <li key={`${p.row}-${p.column}`} className="flex items-start gap-2 text-sm text-red-700">
                <ExclamationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <span className="font-medium">Row {p.row}</span> · {p.column}: {p.message}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Uploads">
        <Table cardView viewKey="own-student-uploads"
          columns={uploadColumns(false)}
          data={data}
          isLoading={isPending}
          exportable={false}
          emptyTitle={isError ? "Could not load your uploads." : "No sheet uploaded yet"}
        />
      </Card>
    </Container>
  );
}

// Every public institution's uploads of admitted students
export function StaffPublicLoansPage() {
  const { data = [], isPending, isError } = useUploads("staff");

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Institution uploads" subtitle="The admitted students each public institution's HLI admin uploaded" />
      <div className="grid gap-4 sm:grid-cols-2">
        <MetricCard title="Institutions" value={new Set(data.map((u) => u.institution.id)).size} icon={<BuildingLibraryIcon className="h-4 w-4" />} />
        <MetricCard title="New students" value={data.reduce((n, u) => n + u.studentsAdded, 0)} icon={<UserPlusIcon className="h-4 w-4" />} />
      </div>
      <Table cardView
        columns={uploadColumns(true)}
        data={data}
        isLoading={isPending}
        exportName="student-uploads"
        emptyTitle={isError ? "Could not load the uploads." : "No institution has uploaded students yet"}
      />
    </Container>
  );
}
